import React, { useState, useEffect, useRef } from 'react';
import { 
  Image, Upload, Save, RefreshCw, Palette, Type, 
  Store, ShoppingCart, ImageIcon, CheckCircle, AlertCircle,
  Eye, EyeOff, X, Camera, FolderOpen, Loader2, Maximize2, Minimize2,
  Droplets, Sun, Moon, Sparkles
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { brandingAPI, uploadAPI } from '../services/api';
import { toast } from 'sonner';

// Predefined color presets
const COLOR_PRESETS = {
  'Ocean Blue': {
    primary_color: '#0a1628',
    secondary_color: '#22c55e',
    accent_color: '#f59e0b',
    text_color: '#1f2937',
    text_light_color: '#6b7280',
    background_color: '#ffffff',
    surface_color: '#f0f9ff',
    border_color: '#bfdbfe',
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#0a1628',
    badge_updated_color: '#22c55e',
    badge_sale_color: '#ef4444',
    button_primary_bg: '#0a1628',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#dbeafe',
    button_secondary_text: '#1d4ed8',
  },
  'Forest Green': {
    primary_color: '#16a34a',
    secondary_color: '#22c55e',
    accent_color: '#eab308',
    text_color: '#14532d',
    text_light_color: '#4d7c0f',
    background_color: '#ffffff',
    surface_color: '#f0fdf4',
    border_color: '#bbf7d0',
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#16a34a',
    badge_updated_color: '#22c55e',
    badge_sale_color: '#dc2626',
    button_primary_bg: '#16a34a',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#dcfce7',
    button_secondary_text: '#15803d',
  },
  'Royal Purple': {
    primary_color: '#7c3aed',
    secondary_color: '#a855f7',
    accent_color: '#f472b6',
    text_color: '#1e1b4b',
    text_light_color: '#6366f1',
    background_color: '#ffffff',
    surface_color: '#f5f3ff',
    border_color: '#c4b5fd',
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#7c3aed',
    badge_updated_color: '#22c55e',
    badge_sale_color: '#ef4444',
    button_primary_bg: '#7c3aed',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#ede9fe',
    button_secondary_text: '#6d28d9',
  },
  'Sunset Orange': {
    primary_color: '#ea580c',
    secondary_color: '#f97316',
    accent_color: '#facc15',
    text_color: '#431407',
    text_light_color: '#9a3412',
    background_color: '#ffffff',
    surface_color: '#fff7ed',
    border_color: '#fed7aa',
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#ea580c',
    badge_updated_color: '#22c55e',
    badge_sale_color: '#dc2626',
    button_primary_bg: '#ea580c',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#ffedd5',
    button_secondary_text: '#c2410c',
  },
  'Crimson Red': {
    primary_color: '#dc2626',
    secondary_color: '#ef4444',
    accent_color: '#f59e0b',
    text_color: '#450a0a',
    text_light_color: '#991b1b',
    background_color: '#ffffff',
    surface_color: '#fef2f2',
    border_color: '#fecaca',
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#dc2626',
    badge_updated_color: '#22c55e',
    badge_sale_color: '#dc2626',
    button_primary_bg: '#dc2626',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#fee2e2',
    button_secondary_text: '#b91c1c',
  },
  'Dark Mode': {
    primary_color: '#60a5fa',
    secondary_color: '#34d399',
    accent_color: '#fbbf24',
    text_color: '#f9fafb',
    text_light_color: '#9ca3af',
    background_color: '#111827',
    surface_color: '#1f2937',
    border_color: '#374151',
    navbar_bg_color: '#1f2937',
    navbar_text_color: '#60a5fa',
    badge_updated_color: '#34d399',
    badge_sale_color: '#f87171',
    button_primary_bg: '#0a1628',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#374151',
    button_secondary_text: '#d1d5db',
  },
  'Teal Cyan': {
    primary_color: '#0891b2',
    secondary_color: '#14b8a6',
    accent_color: '#06b6d4',
    text_color: '#164e63',
    text_light_color: '#0e7490',
    background_color: '#ffffff',
    surface_color: '#ecfeff',
    border_color: '#a5f3fc',
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#0891b2',
    badge_updated_color: '#22c55e',
    badge_sale_color: '#ef4444',
    button_primary_bg: '#0891b2',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#cffafe',
    button_secondary_text: '#0e7490',
  },
  'Rose Pink': {
    primary_color: '#e11d48',
    secondary_color: '#f43f5e',
    accent_color: '#fb7185',
    text_color: '#4c0519',
    text_light_color: '#be123c',
    background_color: '#ffffff',
    surface_color: '#fff1f2',
    border_color: '#fecdd3',
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#e11d48',
    badge_updated_color: '#22c55e',
    badge_sale_color: '#e11d48',
    button_primary_bg: '#e11d48',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#ffe4e6',
    button_secondary_text: '#be123c',
  },
};

// Blue shades for quick selection
const BLUE_SHADES = [
  { name: 'Blue 50', value: '#eff6ff' },
  { name: 'Blue 100', value: '#dbeafe' },
  { name: 'Blue 200', value: '#bfdbfe' },
  { name: 'Blue 300', value: '#93c5fd' },
  { name: 'Blue 400', value: '#60a5fa' },
  { name: 'Blue 500', value: '#3b82f6' },
  { name: 'Blue 600', value: '#2563eb' },
  { name: 'Blue 700', value: '#1d4ed8' },
  { name: 'Blue 800', value: '#1e40af' },
  { name: 'Blue 900', value: '#1e3a8a' },
  { name: 'Dark Blue', value: '#0a1628' },
];

const BrandingSettings = () => {
  const [settings, setSettings] = useState({
    business_name: 'UPLANDS FOOD MARKET',
    business_tagline: 'African & Caribbean groceries delivered fresh',
    logo_url: '',
    logo_width: 250,
    logo_height: 65,
    banner_url: '',
    banner_media: [], // Array of {type: 'image'|'video', url: string}
    cart_icon_url: '',
    // Color settings
    primary_color: '#0a1628',
    secondary_color: '#22c55e',
    accent_color: '#f59e0b',
    text_color: '#1f2937',
    text_light_color: '#6b7280',
    background_color: '#ffffff',
    surface_color: '#f9fafb',
    border_color: '#e5e7eb',
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#0a1628',
    badge_updated_color: '#22c55e',
    badge_sale_color: '#ef4444',
    button_primary_bg: '#0a1628',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#f3f4f6',
    button_secondary_text: '#374151',
    // Image size settings
    logo_size: 100,
    banner_height: 100,
    cart_icon_size: 100,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [activeUpload, setActiveUpload] = useState(null);
  const [uploading, setUploading] = useState({});
  const [activeColorTab, setActiveColorTab] = useState('presets');
  
  // File input refs
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const bannerMediaInputRef = useRef(null);
  const cartIconInputRef = useRef(null);

  // Fetch current settings
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await brandingAPI.get();
      setSettings(prev => ({
        ...prev,
        ...data,
      }));
    } catch (error) {
      console.error('Error fetching branding settings:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Handle input change
  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Apply a color preset
  const applyPreset = (presetName) => {
    const preset = COLOR_PRESETS[presetName];
    if (preset) {
      setSettings(prev => ({ ...prev, ...preset }));
      toast.success(`Applied "${presetName}" theme!`);
    }
  };

  // Save all settings
  const handleSave = async () => {
    try {
      setSaving(true);
      await brandingAPI.update(settings);
      toast.success('Branding settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  // Handle image URL update
  const handleImageUpdate = async (imageType, url) => {
    if (!url.trim()) {
      toast.error('Please enter a valid image URL');
      return;
    }

    try {
      setActiveUpload(imageType);
      const fieldMap = {
        logo: 'logo_url',
        banner: 'banner_url',
        cart_icon: 'cart_icon_url',
      };
      
      await brandingAPI.uploadImage(imageType, url);
      setSettings(prev => ({ ...prev, [fieldMap[imageType]]: url }));
      toast.success(`${imageType.replace('_', ' ')} image updated!`);
    } catch (error) {
      toast.error(`Failed to update ${imageType} image`);
    } finally {
      setActiveUpload(null);
    }
  };

  // Clear an image
  const handleClearImage = (field) => {
    setSettings(prev => ({ ...prev, [field]: '' }));
  };

  // Handle file upload from camera or gallery
  const handleFileUpload = async (e, imageType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(prev => ({ ...prev, [imageType]: true }));

    try {
      const result = await uploadAPI.uploadImage(file);
      
      const fieldMap = {
        logo: 'logo_url',
        banner: 'banner_url',
        cart_icon: 'cart_icon_url',
      };
      
      setSettings(prev => ({ ...prev, [fieldMap[imageType]]: result.url }));
      toast.success(`${imageType.replace('_', ' ')} image uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [imageType]: false }));
      e.target.value = '';
    }
  };

  // Open file picker
  const openFilePicker = (inputRef, captureType = null) => {
    if (inputRef.current) {
      if (captureType) {
        inputRef.current.setAttribute('capture', captureType);
      } else {
        inputRef.current.removeAttribute('capture');
      }
      inputRef.current.click();
    }
  };

  // Handle banner media upload (for carousel)
  const handleBannerMediaUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(prev => ({ ...prev, banner_media: true }));

    try {
      const newMedia = [];
      
      for (const file of files) {
        // Check file type (image or video)
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        
        if (!isImage && !isVideo) {
          toast.error(`${file.name} is not a valid image or video file`);
          continue;
        }

        if (file.size > 20 * 1024 * 1024) { // 20MB limit for videos
          toast.error(`${file.name} is too large (max 20MB)`);
          continue;
        }

        const result = await uploadAPI.uploadImage(file);
        newMedia.push({
          type: isVideo ? 'video' : 'image',
          url: result.url
        });
      }

      if (newMedia.length > 0) {
        setSettings(prev => ({
          ...prev,
          banner_media: [...(prev.banner_media || []), ...newMedia]
        }));
        toast.success(`Added ${newMedia.length} media item(s) to carousel`);
      }
    } catch (error) {
      console.error('Banner media upload error:', error);
      toast.error(error.message || 'Failed to upload media');
    } finally {
      setUploading(prev => ({ ...prev, banner_media: false }));
      e.target.value = '';
    }
  };

  // Remove a banner media item
  const removeBannerMedia = (index) => {
    setSettings(prev => ({
      ...prev,
      banner_media: prev.banner_media.filter((_, i) => i !== index)
    }));
    toast.success('Media removed from carousel');
  };

  // Color picker component
  const ColorPicker = ({ label, field, description }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      {description && <p className="text-xs text-gray-500">{description}</p>}
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={settings[field] || '#000000'}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-200 p-0.5"
        />
        <Input
          value={settings[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-28 font-mono text-sm"
          placeholder="#000000"
        />
        <div 
          className="w-8 h-8 rounded border border-gray-300"
          style={{ backgroundColor: settings[field] }}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="branding-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1 flex items-center gap-2">
            <Palette className="w-7 h-7 text-blue-500" />
            Branding Settings
          </h1>
          <p className="text-gray-500">Customize your store&apos;s appearance and colors</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPreviewMode(!previewMode)}
            data-testid="toggle-preview-btn"
          >
            {previewMode ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {previewMode ? 'Hide Preview' : 'Show Preview'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            data-testid="save-branding-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Preview Section */}
      {previewMode && (
        <Card className="mb-6 overflow-hidden" data-testid="branding-preview">
          <div className="p-4 border-b" style={{ backgroundColor: settings.surface_color }}>
            <h3 className="font-semibold flex items-center gap-2" style={{ color: settings.text_color }}>
              <Eye className="w-4 h-4" />
              Live Preview
            </h3>
          </div>
          
          {/* Navbar Preview */}
          <div className="p-4 border-b" style={{ backgroundColor: settings.navbar_bg_color }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {settings.logo_url ? (
                  <img 
                    src={settings.logo_url} 
                    alt="Logo" 
                    className="w-10 h-10 rounded-lg object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                )}
                <span className="font-bold text-lg" style={{ color: settings.navbar_text_color }}>
                  {settings.business_name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  className="px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: settings.button_primary_bg, color: settings.button_primary_text }}
                >
                  Shop
                </button>
                <button 
                  className="px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: settings.button_secondary_bg, color: settings.button_secondary_text }}
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
          
          {/* Banner Preview */}
          <div className="relative h-32" style={{ backgroundColor: settings.surface_color }}>
            {settings.banner_url ? (
              <img 
                src={settings.banner_url} 
                alt="Banner" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.secondary_color})` }} />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
            <div className="absolute inset-0 flex items-center px-6">
              <div>
                <h2 className="text-white text-2xl font-bold">{settings.business_name}</h2>
                <p className="text-white/90 text-sm">{settings.business_tagline}</p>
              </div>
            </div>
          </div>
          
          {/* Product Card Preview */}
          <div className="p-4" style={{ backgroundColor: settings.background_color }}>
            <p className="text-sm mb-3" style={{ color: settings.text_light_color }}>Product Card Preview:</p>
            <div className="flex gap-4">
              <div 
                className="w-48 rounded-lg overflow-hidden shadow-md"
                style={{ backgroundColor: settings.surface_color, borderColor: settings.border_color, borderWidth: '1px' }}
              >
                <div className="h-32 relative" style={{ backgroundColor: settings.border_color }}>
                  <ImageIcon className="w-8 h-8 absolute inset-0 m-auto" style={{ color: settings.text_light_color }} />
                  <span 
                    className="absolute bottom-2 left-2 px-2 py-0.5 text-xs font-bold rounded-full text-white"
                    style={{ backgroundColor: settings.badge_updated_color }}
                  >
                    Updated
                  </span>
                </div>
                <div className="p-3">
                  <h4 className="font-semibold text-sm" style={{ color: settings.text_color }}>Sample Product</h4>
                  <p className="text-lg font-bold" style={{ color: settings.primary_color }}>$9.99</p>
                  <button 
                    className="w-full mt-2 py-1.5 rounded text-sm font-medium"
                    style={{ backgroundColor: settings.button_primary_bg, color: settings.button_primary_text }}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Checkout Button Preview */}
          <div className="p-4" style={{ backgroundColor: settings.surface_color }}>
            <p className="text-sm mb-2" style={{ color: settings.text_light_color }}>Checkout Button Preview:</p>
            <div className="flex items-center gap-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: settings.secondary_color }}
              >
                {settings.cart_icon_url ? (
                  <img 
                    src={settings.cart_icon_url} 
                    alt="Cart" 
                    className="w-8 h-8 object-contain"
                  />
                ) : (
                  <ShoppingCart className="w-7 h-7 text-white" />
                )}
              </div>
              <span className="text-sm" style={{ color: settings.text_light_color }}>Floating checkout button</span>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-6">
        {/* Color Theme Settings */}
        <Card className="p-6">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Droplets className="w-5 h-5 text-blue-500" />
            Color Theme
            <Badge className="bg-blue-100 text-blue-700 ml-2">New</Badge>
          </h2>
          
          <Tabs value={activeColorTab} onValueChange={setActiveColorTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="presets" className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Presets
              </TabsTrigger>
              <TabsTrigger value="blue-shades" className="flex items-center gap-2">
                <Droplets className="w-4 h-4" />
                Blue Shades
              </TabsTrigger>
              <TabsTrigger value="custom" className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Custom
              </TabsTrigger>
            </TabsList>
            
            {/* Presets Tab */}
            <TabsContent value="presets" className="space-y-4">
              <p className="text-sm text-gray-600">Choose a pre-designed color theme:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(COLOR_PRESETS).map(([name, colors]) => (
                  <button
                    key={name}
                    onClick={() => applyPreset(name)}
                    className="p-3 rounded-lg border-2 hover:border-blue-500 transition-all group"
                    style={{ borderColor: settings.primary_color === colors.primary_color ? colors.primary_color : '#e5e7eb' }}
                  >
                    <div className="flex gap-1 mb-2">
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: colors.primary_color }} />
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: colors.secondary_color }} />
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: colors.accent_color }} />
                    </div>
                    <p className="text-xs font-medium text-gray-700 group-hover:text-blue-600">{name}</p>
                  </button>
                ))}
              </div>
            </TabsContent>
            
            {/* Blue Shades Tab */}
            <TabsContent value="blue-shades" className="space-y-4">
              <p className="text-sm text-gray-600">Quick select from blue color palette:</p>
              <div className="grid grid-cols-11 gap-1">
                {BLUE_SHADES.map((shade) => (
                  <button
                    key={shade.value}
                    onClick={() => handleChange('primary_color', shade.value)}
                    className="aspect-square rounded-lg border-2 hover:scale-110 transition-transform relative group"
                    style={{ 
                      backgroundColor: shade.value,
                      borderColor: settings.primary_color === shade.value ? '#000' : 'transparent'
                    }}
                    title={shade.name}
                  >
                    {settings.primary_color === shade.value && (
                      <CheckCircle className="w-4 h-4 absolute inset-0 m-auto text-white drop-shadow-lg" />
                    )}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-lg" style={{ backgroundColor: settings.primary_color }} />
                <div>
                  <p className="text-sm font-medium">Selected Primary Color</p>
                  <p className="text-xs text-gray-500 font-mono">{settings.primary_color}</p>
                </div>
              </div>
            </TabsContent>
            
            {/* Custom Colors Tab */}
            <TabsContent value="custom" className="space-y-6">
              {/* Main Colors */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700 flex items-center gap-2">
                  <Sun className="w-4 h-4" />
                  Main Colors
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <ColorPicker label="Primary Color" field="primary_color" description="Main brand color" />
                  <ColorPicker label="Secondary Color" field="secondary_color" description="Checkout/CTA buttons" />
                  <ColorPicker label="Accent Color" field="accent_color" description="Highlights" />
                </div>
              </div>
              
              {/* Text Colors */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-gray-700 flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Text Colors
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <ColorPicker label="Main Text" field="text_color" description="Primary text color" />
                  <ColorPicker label="Secondary Text" field="text_light_color" description="Muted/light text" />
                </div>
              </div>
              
              {/* Background Colors */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-gray-700 flex items-center gap-2">
                  <Moon className="w-4 h-4" />
                  Background Colors
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <ColorPicker label="Background" field="background_color" description="Main background" />
                  <ColorPicker label="Surface" field="surface_color" description="Cards/panels" />
                  <ColorPicker label="Border" field="border_color" description="Borders/dividers" />
                </div>
              </div>
              
              {/* Navbar Colors */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-gray-700">Navbar Colors</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <ColorPicker label="Navbar Background" field="navbar_bg_color" />
                  <ColorPicker label="Navbar Text" field="navbar_text_color" />
                </div>
              </div>
              
              {/* Badge Colors */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-gray-700">Badge Colors</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <ColorPicker label="Updated Badge" field="badge_updated_color" />
                  <ColorPicker label="Sale Badge" field="badge_sale_color" />
                </div>
              </div>
              
              {/* Button Colors */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-gray-700">Button Colors</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <ColorPicker label="Primary Button BG" field="button_primary_bg" />
                  <ColorPicker label="Primary Button Text" field="button_primary_text" />
                  <ColorPicker label="Secondary Button BG" field="button_secondary_bg" />
                  <ColorPicker label="Secondary Button Text" field="button_secondary_text" />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Business Info */}
        <Card className="p-6">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Store className="w-5 h-5" />
            Business Information
          </h2>
          
          <div className="grid gap-4">
            <div>
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                value={settings.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                placeholder="Your Business Name"
                data-testid="business-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="business_tagline">Tagline</Label>
              <Input
                id="business_tagline"
                value={settings.business_tagline}
                onChange={(e) => handleChange('business_tagline', e.target.value)}
                placeholder="Your business tagline"
                data-testid="business-tagline-input"
              />
            </div>
          </div>
        </Card>

        {/* Logo Settings */}
        <Card className="p-6">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Logo
            {settings.logo_url && <Badge className="bg-green-100 text-green-700">Custom</Badge>}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Label htmlFor="logo_url">Logo Image</Label>
              
              <div className="flex gap-2 flex-wrap">
                <input
                  type="file"
                  ref={logoInputRef}
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'logo')}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openFilePicker(logoInputRef, 'environment')}
                  disabled={uploading.logo}
                  className="flex items-center gap-2"
                  data-testid="logo-camera-btn"
                >
                  {uploading.logo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  Camera
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openFilePicker(logoInputRef)}
                  disabled={uploading.logo}
                  className="flex items-center gap-2"
                  data-testid="logo-gallery-btn"
                >
                  {uploading.logo ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />}
                  Gallery
                </Button>
              </div>

              <div>
                <Label className="text-xs text-gray-500">Or enter URL directly:</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="logo_url"
                    value={settings.logo_url}
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                    data-testid="logo-url-input"
                  />
                  {settings.logo_url && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleClearImage('logo_url')}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">Recommended: Square image, 200x200px or larger</p>
              
              {settings.logo_url && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <Label className="flex items-center gap-2 mb-2">
                    <Maximize2 className="w-4 h-4" />
                    Logo Size: {settings.logo_size || 100}%
                  </Label>
                  <Slider
                    value={[settings.logo_size || 100]}
                    onValueChange={([value]) => handleChange('logo_size', value)}
                    min={50}
                    max={150}
                    step={5}
                    className="w-full"
                  />
                </div>
              )}
              
              {/* Logo Dimensions */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg space-y-3">
                <Label className="flex items-center gap-2 text-blue-700 font-medium">
                  <Maximize2 className="w-4 h-4" />
                  Logo Dimensions (pixels)
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Max Width</Label>
                    <Input
                      type="number"
                      value={settings.logo_width || 250}
                      onChange={(e) => handleChange('logo_width', parseInt(e.target.value) || 250)}
                      min={50}
                      max={500}
                      className="mt-1"
                      data-testid="logo-width-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Max Height</Label>
                    <Input
                      type="number"
                      value={settings.logo_height || 65}
                      onChange={(e) => handleChange('logo_height', parseInt(e.target.value) || 65)}
                      min={30}
                      max={200}
                      className="mt-1"
                      data-testid="logo-height-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-blue-600">Adjust how the logo appears in the navbar</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              {settings.logo_url ? (
                <div className="relative">
                  <img 
                    src={settings.logo_url} 
                    alt="Logo Preview" 
                    className="rounded-lg object-cover border-2 border-gray-200 transition-all"
                    style={{ 
                      width: `${(settings.logo_size || 100) * 0.96}px`,
                      height: `${(settings.logo_size || 100) * 0.96}px`
                    }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100x100?text=Invalid+URL';
                    }}
                  />
                  <CheckCircle className="absolute -bottom-2 -right-2 w-6 h-6 text-green-500 bg-white rounded-full" />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <ImageIcon className="w-8 h-8 text-gray-400 mx-auto" />
                    <span className="text-xs text-gray-400">No logo</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Banner Settings */}
        <Card className="p-6">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5" />
            Hero Banner
            {settings.banner_url && <Badge className="bg-green-100 text-green-700">Custom</Badge>}
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-3">
              <Label htmlFor="banner_url">Banner Image</Label>
              
              <div className="flex gap-2 flex-wrap">
                <input
                  type="file"
                  ref={bannerInputRef}
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'banner')}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openFilePicker(bannerInputRef, 'environment')}
                  disabled={uploading.banner}
                  className="flex items-center gap-2"
                >
                  {uploading.banner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  Camera
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openFilePicker(bannerInputRef)}
                  disabled={uploading.banner}
                  className="flex items-center gap-2"
                >
                  {uploading.banner ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />}
                  Gallery
                </Button>
              </div>

              <div>
                <Label className="text-xs text-gray-500">Or enter URL directly:</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="banner_url"
                    value={settings.banner_url}
                    onChange={(e) => handleChange('banner_url', e.target.value)}
                    placeholder="https://example.com/banner.jpg"
                  />
                  {settings.banner_url && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleClearImage('banner_url')}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">Recommended: Wide image, 1200x300px or larger</p>
              
              {settings.banner_url && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="flex items-center gap-2 mb-2">
                    <Maximize2 className="w-4 h-4" />
                    Banner Height: {settings.banner_height || 100}%
                  </Label>
                  <Slider
                    value={[settings.banner_height || 100]}
                    onValueChange={([value]) => handleChange('banner_height', value)}
                    min={50}
                    max={200}
                    step={10}
                    className="w-full"
                  />
                </div>
              )}
            </div>
            
            {settings.banner_url && (
              <div 
                className="relative rounded-lg overflow-hidden transition-all"
                style={{ height: `${(settings.banner_height || 100) * 1.28}px` }}
              >
                <img 
                  src={settings.banner_url} 
                  alt="Banner Preview" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
              </div>
            )}
            
            {/* Banner Media Carousel Section */}
            <div className="mt-6 pt-6 border-t">
              <Label className="flex items-center gap-2 mb-3 font-semibold text-gray-800">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Banner Carousel (Multiple Images/Videos)
                <Badge className="bg-purple-100 text-purple-700 ml-2">New</Badge>
              </Label>
              <p className="text-xs text-gray-500 mb-3">
                Upload multiple images or videos to create an auto-rotating carousel for your hero banner.
              </p>
              
              {/* Upload Button */}
              <div className="flex gap-2 flex-wrap mb-4">
                <input
                  type="file"
                  ref={bannerMediaInputRef}
                  accept="image/*,video/*"
                  multiple
                  onChange={handleBannerMediaUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openFilePicker(bannerMediaInputRef)}
                  disabled={uploading.banner_media}
                  className="flex items-center gap-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300"
                  data-testid="banner-media-upload-btn"
                >
                  {uploading.banner_media ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Add Images/Videos
                </Button>
              </div>
              
              {/* Media Preview Grid */}
              {settings.banner_media && settings.banner_media.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {settings.banner_media.map((media, index) => (
                    <div 
                      key={index} 
                      className="relative aspect-video rounded-lg overflow-hidden border-2 border-gray-200 group"
                    >
                      {media.type === 'video' ? (
                        <video 
                          src={media.url}
                          className="w-full h-full object-cover"
                          muted
                        />
                      ) : (
                        <img 
                          src={media.url}
                          alt={`Banner ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {/* Media type badge */}
                      <span className={`absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        media.type === 'video' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-green-500 text-white'
                      }`}>
                        {media.type === 'video' ? 'Video' : 'Image'}
                      </span>
                      {/* Remove button */}
                      <button
                        onClick={() => removeBannerMedia(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {/* Order number */}
                      <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                        #{index + 1}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-center">
                  <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No carousel media yet</p>
                  <p className="text-xs text-gray-400">Upload images or videos to create an auto-rotating banner</p>
                </div>
              )}
              
              {settings.banner_media && settings.banner_media.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {settings.banner_media.length} item(s) in carousel • Carousel will auto-rotate every 5 seconds
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Cart Icon Settings */}
        <Card className="p-6">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Checkout Button Icon
            {settings.cart_icon_url && <Badge className="bg-green-100 text-green-700">Custom</Badge>}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Label htmlFor="cart_icon_url">Cart Icon Image</Label>
              
              <div className="flex gap-2 flex-wrap">
                <input
                  type="file"
                  ref={cartIconInputRef}
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'cart_icon')}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openFilePicker(cartIconInputRef, 'environment')}
                  disabled={uploading.cart_icon}
                  className="flex items-center gap-2"
                >
                  {uploading.cart_icon ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  Camera
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openFilePicker(cartIconInputRef)}
                  disabled={uploading.cart_icon}
                  className="flex items-center gap-2"
                >
                  {uploading.cart_icon ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />}
                  Gallery
                </Button>
              </div>

              <div>
                <Label className="text-xs text-gray-500">Or enter URL directly:</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="cart_icon_url"
                    value={settings.cart_icon_url}
                    onChange={(e) => handleChange('cart_icon_url', e.target.value)}
                    placeholder="https://example.com/cart-icon.png"
                  />
                  {settings.cart_icon_url && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleClearImage('cart_icon_url')}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">Recommended: Square image with transparent background</p>
              
              {settings.cart_icon_url && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <Label className="flex items-center gap-2 mb-2">
                    <Maximize2 className="w-4 h-4" />
                    Button Size: {settings.cart_icon_size || 100}%
                  </Label>
                  <Slider
                    value={[settings.cart_icon_size || 100]}
                    onValueChange={([value]) => handleChange('cart_icon_size', value)}
                    min={70}
                    max={130}
                    step={5}
                    className="w-full"
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-center">
              <div 
                className="rounded-full flex items-center justify-center shadow-lg transition-all"
                style={{ 
                  backgroundColor: settings.secondary_color,
                  width: `${(settings.cart_icon_size || 100) * 0.8}px`,
                  height: `${(settings.cart_icon_size || 100) * 0.8}px`
                }}
              >
                {settings.cart_icon_url ? (
                  <img 
                    src={settings.cart_icon_url} 
                    alt="Cart Icon Preview" 
                    className="object-contain"
                    style={{ 
                      width: `${(settings.cart_icon_size || 100) * 0.4}px`,
                      height: `${(settings.cart_icon_size || 100) * 0.4}px`
                    }}
                  />
                ) : (
                  <ShoppingCart className="w-8 h-8 text-white" />
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Help Info */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-700">Tips for Customization:</p>
              <ul className="text-blue-600 mt-1 space-y-1">
                <li>• Use the &quot;Presets&quot; tab for quick theme changes</li>
                <li>• &quot;Blue Shades&quot; tab provides various blue intensities</li>
                <li>• &quot;Custom&quot; tab allows full color customization</li>
                <li>• Enable &quot;Show Preview&quot; to see changes in real-time</li>
                <li>• Changes are applied after clicking &quot;Save Changes&quot;</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BrandingSettings;
